import React, { useState, useEffect } from 'react';
import {
  Settings as SettingsIcon,
  List as ListIcon,
  CloudQueue as CloudIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { ChatConfigResponse, ChatProviderType } from '../api/ApiClient';
import { useSystem } from '../hooks/api/config';
import AuthService from '../services/AuthService';
import {
  useGetChatConfigs,
  useGetCurrentChatConfig,
  useGetChatProviders,
  useSaveChatConfig,
  useDeleteChatConfig,
  useSetDefaultChatConfig,
  useTestChatConfig,
  useResetToEnvConfig
} from '@/hooks/chat/useChatConfig';
import { log } from '../utils/errors/logger';
import { PageLayout } from '../components/ui/PageLayout';
import {
  AdminTabs,
  type AdminTab,
} from '../components/admin';
import { useSnackbar } from '../contexts/SnackbarContext';

// Import modular components from ChatConfig feature directory
import CurrentConfigTab from '@/components/ChatConfig/CurrentConfigTab';
import AllConfigsTab from '@/components/ChatConfig/AllConfigsTab';
import ProvidersTab from '@/components/ChatConfig/ProvidersTab';
import ChatConfigEditDialog from '@/components/ChatConfig/ChatConfigEditDialog';
import TestResultDialog from '@/components/ChatConfig/TestResultDialog';
import DeleteConfirmDialog from '@/components/ChatConfig/DeleteConfirmDialog';
import LoadingSpinner from '@/components/ChatConfig/LoadingSpinner';

export default function ChatConfig() {
  const { t } = useTranslation();
  const { system } = useSystem();
  const { showSuccess, showError } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);

  // Get user data to check role
  const user = AuthService.getUser();

  // Dialog states
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<Partial<ChatConfigResponse> | null>(null);
  const [configToDelete, setConfigToDelete] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<any>(null);

  // TanStack Query hooks
  const { data: configs = [], isLoading: configsLoading, error: configsError } = useGetChatConfigs();
  const { data: currentConfig, isLoading: currentLoading, error: currentError } = useGetCurrentChatConfig();
  const { data: providers = [], isLoading: providersLoading, error: providersError } = useGetChatProviders();

  // Mutations
  const saveMutation = useSaveChatConfig();
  const deleteMutation = useDeleteChatConfig();
  const setDefaultMutation = useSetDefaultChatConfig();
  const testMutation = useTestChatConfig();
  const resetMutation = useResetToEnvConfig();

  // Combined loading state
  const loading = configsLoading || currentLoading || providersLoading ||
                 saveMutation.isPending || deleteMutation.isPending ||
                 setDefaultMutation.isPending || testMutation.isPending ||
                 resetMutation.isPending;

  // Handle query errors
  useEffect(() => {
    const queryError = configsError || currentError || providersError;
    if (queryError) {
      showError(t('chatConfig.messages.loadFailed'));
      log.error('Chat config query error', {
        error: queryError?.message || queryError?.toString() || 'Unknown error'
      });
    }
  }, [configsError, currentError, providersError, t, showError]);

  // Handler functions using mutations
  const handleTestConfig = async (config: Partial<ChatConfigResponse>) => {
    // Single tenant - no organization check needed

    try {
      const result = await testMutation.mutateAsync(config);
      setTestResult(result);
      setTestDialogOpen(true);
      log.user.action('chat_config_tested', 'ChatConfig', { model: config.model });
    } catch (error) {
      showError(t('chatConfig.messages.testFailed'));
    }
  };

  const handleSaveConfig = async (config: Partial<ChatConfigResponse>) => {
    // Single tenant - no organization check needed

    try {
      await saveMutation.mutateAsync(config);
      showSuccess(t('chatConfig.messages.saveSuccess'));
      setConfigDialogOpen(false);
      log.user.action('chat_config_saved', 'ChatConfig', {
        model: config.model,
        provider: config.provider_type
      });
    } catch (error) {
      showError(t('chatConfig.messages.saveFailed'));
    }
  };

  const handleDeleteConfig = async (configId: string) => {
    // Single tenant - no organization check needed

    try {
      await deleteMutation.mutateAsync(configId);
      showSuccess(t('chatConfig.messages.deleteSuccess'));
      setDeleteDialogOpen(false);
      log.user.action('chat_config_deleted', 'ChatConfig', { configId });
    } catch (error) {
      showError(t('chatConfig.messages.deleteFailed'));
    }
  };

  const handleSetDefault = async (config: ChatConfigResponse) => {
    // Single tenant - no organization check needed

    try {
      await setDefaultMutation.mutateAsync(config);
      showSuccess(t('chatConfig.messages.saveSuccess'));
      log.user.action('chat_config_set_default', 'ChatConfig', {
        model: config.model,
        configId: config.id
      });
    } catch (error) {
      showError(t('chatConfig.messages.saveFailed'));
    }
  };

  const handleResetToEnv = async () => {
    // Single tenant - no organization check needed

    try {
      await resetMutation.mutateAsync();
      showSuccess(t('chatConfig.messages.resetSuccess'));
      log.user.action('chat_config_reset_to_env', 'ChatConfig');
    } catch (error) {
      showError(t('chatConfig.messages.resetFailed'));
    }
  };

  const openEditDialog = (config?: ChatConfigResponse) => {
    if (config) {
      setEditingConfig({ ...config });
    } else {
      // Default to first available provider with its default model
      const firstProvider = providers[0];
      const defaultModel = firstProvider?.default_models?.[0] || firstProvider?.default_model || '';

      setEditingConfig({
        provider_type: firstProvider?.value as ChatProviderType || ChatProviderType.Openai,
        display_name: '',
        description: '',
        model: defaultModel,
        temperature: 0.7,
        max_tokens: 1000,
        timeout: 30,
        streaming: true,
        system_message: '',
        provider_config: {},
        is_active: true,
        is_default: false,
      });
    }
    setConfigDialogOpen(true);
  };

  const handleDeleteClick = (configId: string) => {
    setConfigToDelete(configId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (configToDelete) {
      handleDeleteConfig(configToDelete);
    }
  };

  // Define tabs with standardized structure
  const tabs: AdminTab[] = [
    {
      label: t('chatConfig.tabs.currentConfiguration'),
      icon: <SettingsIcon />,
      content: (
        <CurrentConfigTab
          currentConfig={currentConfig}
          onEdit={openEditDialog}
          onTest={handleTestConfig}
          onResetToEnv={handleResetToEnv}
        />
      ),
    },
    {
      label: t('chatConfig.tabs.allConfigurations'),
      icon: <ListIcon />,
      content: (
        <AllConfigsTab
          configs={configs}
          onAdd={() => openEditDialog()}
          onEdit={openEditDialog}
          onTest={handleTestConfig}
          onSetDefault={handleSetDefault}
          onDelete={handleDeleteClick}
        />
      ),
    },
    {
      label: t('chatConfig.tabs.availableProviders'),
      icon: <CloudIcon />,
      content: <ProvidersTab providers={providers} />,
    },
  ];

  return (
    <>
      <PageLayout
        title={t('chatConfig.title')}
        isLoading={configsLoading || currentLoading || providersLoading}
        maxWidth="xl"
      >
        <AdminTabs
          tabs={tabs}
          value={tabValue}
          onChange={setTabValue}
          variant="standard"
        />
      </PageLayout>

      {/* Dialogs */}
      <ChatConfigEditDialog
        open={configDialogOpen}
        config={editingConfig}
        providers={providers}
        onClose={() => setConfigDialogOpen(false)}
        onSave={handleSaveConfig}
        onTest={handleTestConfig}
      />

      <TestResultDialog
        open={testDialogOpen}
        testResult={testResult}
        onClose={() => setTestDialogOpen(false)}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
      />

      {/* Loading and Notifications */}
      <LoadingSpinner loading={loading} />
    </>
  );
}
